import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, MapPin, Pencil, Power, Star } from "lucide-react";
import Sparkline from "@/components/Sparkline";
import { distanceMiles } from "@/lib/geo";

const TAG_OPTIONS = ["byob", "outdoor", "quiet", "lively", "casual", "upscale", "vegetarian_friendly", "family_friendly", "romantic", "parking"];

function RestaurantModal({ open, onOpenChange, initial, onSaved, cuisineOptions = [] }) {
  const empty = { name:"", cuisine:"", city:"", state:"", price_level:2, latitude:"", longitude:"", address:"", zip_code:"", hours:"", reservation_required:true, website:"", phone:"", tags:[], active:true };
  const [f, setF] = useState(empty);
  useEffect(() => { if (initial) setF({...empty, ...initial, latitude: initial.latitude?.toString(), longitude: initial.longitude?.toString(), tags: initial.tags || []}); else setF(empty); }, [initial, open]);
  const toggleTag = (t) => setF(x => ({...x, tags: x.tags.includes(t) ? x.tags.filter(y=>y!==t) : [...x.tags, t]}));
  const titleCase = (s) => (s || "").trim().replace(/\s+/g, " ").split(" ").map(w =>
    /^(BBQ|BYOB)$/i.test(w) ? w.toUpperCase() : (w[0]?.toUpperCase() + w.slice(1).toLowerCase())
  ).join(" ");
  const save = async () => {
    try {
      const body = { ...f, cuisine: titleCase(f.cuisine), latitude: parseFloat(f.latitude), longitude: parseFloat(f.longitude), price_level: Number(f.price_level) };
      if (initial?.id) await api.patch(`/restaurants/${initial.id}`, body);
      else await api.post("/restaurants", body);
      toast.success(initial?.id ? "Restaurant updated" : "Restaurant added");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit restaurant" : "Add restaurant"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2"><Label>Name</Label><Input data-testid="restaurant-name-input" value={f.name} onChange={e=>setF({...f, name:e.target.value})}/></div>
          <div>
            <Label>Cuisine</Label>
            <Input
              value={f.cuisine}
              onChange={e=>setF({...f, cuisine:e.target.value})}
              onBlur={e=>setF({...f, cuisine: titleCase(e.target.value)})}
              list="cuisine-options-list"
              data-testid="restaurant-cuisine-input"
              placeholder="Pick or type"
            />
            <datalist id="cuisine-options-list">
              {cuisineOptions.map(c => <option key={c} value={c} />)}
            </datalist>
            <div className="text-[10px] text-muted-foreground mt-1">Pick from the list to avoid duplicates.</div>
          </div>
          <div><Label>Price 1-4</Label><Input type="number" min={1} max={4} value={f.price_level} onChange={e=>setF({...f, price_level:e.target.value})}/></div>
          <div className="md:col-span-2"><Label>Address</Label><Input value={f.address} onChange={e=>setF({...f, address:e.target.value})}/></div>
          <div><Label>City</Label><Input value={f.city} onChange={e=>setF({...f, city:e.target.value})}/></div>
          <div><Label>State</Label><Input value={f.state} onChange={e=>setF({...f, state:e.target.value})}/></div>
          <div><Label>ZIP</Label><Input value={f.zip_code} onChange={e=>setF({...f, zip_code:e.target.value})}/></div>
          <div><Label>Latitude</Label><Input value={f.latitude} onChange={e=>setF({...f, latitude:e.target.value})}/></div>
          <div><Label>Longitude</Label><Input value={f.longitude} onChange={e=>setF({...f, longitude:e.target.value})}/></div>
          <div><Label>Phone</Label><Input value={f.phone||""} onChange={e=>setF({...f, phone:e.target.value})}/></div>
          <div className="md:col-span-3"><Label>Website</Label><Input value={f.website||""} onChange={e=>setF({...f, website:e.target.value})}/></div>
          <div className="md:col-span-3"><Label>Hours</Label><Input value={f.hours||""} onChange={e=>setF({...f, hours:e.target.value})}/></div>
          <div className="md:col-span-3">
            <Label>Tags (used for member characteristic matching)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {TAG_OPTIONS.map(t=>(
                <button key={t} type="button" onClick={()=>toggleTag(t)} className={`text-xs px-3 py-1 rounded-full border ${f.tags.includes(t)?"bg-primary text-primary-foreground border-primary":"hover:bg-secondary"}`}>{t.replace("_"," ")}</button>
              ))}
            </div>
          </div>
          <label className="md:col-span-3 flex items-center gap-2 mt-2"><Switch checked={f.reservation_required} onCheckedChange={v=>setF({...f, reservation_required:v})}/><span className="text-sm">Reservation required</span></label>
          <label className="md:col-span-3 flex items-center gap-2"><Switch checked={f.active} onCheckedChange={v=>setF({...f, active:v})}/><span className="text-sm">Active</span></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
          <Button data-testid="submit-restaurant-btn" onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Restaurants() {
  const [rows, setRows] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [features, setFeatures] = useState({ google_maps: false });
  const [showInactive, setShowInactive] = useState(false);
  const [rOpen, setROpen] = useState(false); const [rEdit, setREdit] = useState(null);
  const [suggestOpen, setSuggestOpen] = useState(null); // {restaurant, tags[], hits?}
  const [nearHouseholdId, setNearHouseholdId] = useState("");
  const [nearMiles, setNearMiles] = useState(0); // 0 = All
  const [nameFilter, setNameFilter] = useState("");
  const [cuisineFilter, setCuisineFilter] = useState("");
  const [visibleCount, setVisibleCount] = useState(60);

  const [discOpen, setDiscOpen] = useState(false);
  const [discHouseholdId, setDiscHouseholdId] = useState("");
  const [discKeyword, setDiscKeyword] = useState("restaurant");
  const [discRadius, setDiscRadius] = useState(8000);
  const [discResults, setDiscResults] = useState([]);
  const [discLoading, setDiscLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => Promise.all([
    api.get("/restaurants").then(r=>r.data),
    api.get("/households").then(r=>r.data),
    api.get("/features").then(r=>r.data),
  ]).then(([r, h, f]) => { setRows(r); setHouseholds(h); setFeatures(f); if(h[0] && !discHouseholdId) setDiscHouseholdId(h[0].id); if(h[0] && !nearHouseholdId) setNearHouseholdId(h[0].id); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (item) => {
    try {
      await api.patch(`/restaurants/${item.id}`, { active: !item.active });
      toast.success(!item.active ? "Reactivated" : "Deactivated — visits and ratings preserved");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const discover = async () => {
    const h = households.find(x => x.id === discHouseholdId);
    if (!h) return toast.error("Select a household");
    setDiscLoading(true); setDiscResults([]);
    try {
      const { data } = await api.get("/restaurants/discover", { params: { lat: h.latitude, lng: h.longitude, radius_m: discRadius, keyword: discKeyword }});
      setDiscResults(data);
      toast.success(`Found ${data.length} places`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setDiscLoading(false); }
  };

  const runBulkLoad = async () => {
    const h = households.find(x => x.id === (nearHouseholdId || households[0]?.id));
    if (!h) return toast.error("Add a household first — it's used as the search anchor.");
    if (!window.confirm(`Sweep 50-mile radius from ${h.name} and import every restaurant Google Places knows about? This may take 1–2 minutes and uses your Google Maps quota.`)) return;
    setBulkLoading(true); setBulkResult(null);
    try {
      const { data } = await api.post("/admin/bulk-load-restaurants", {
        center_lat: h.latitude, center_lng: h.longitude,
        radius_miles: 50, step_miles: 8,
      });
      setBulkResult(data);
      toast.success(`Bulk load complete — ${data.inserted} new · ${data.skipped_existing} already existed`);
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBulkLoading(false); }
  };

  const importPlace = async (p) => {
    try {
      const { data } = await api.post("/restaurants/from-place", { ...p, cuisine: discKeyword });
      const rest = data.restaurant;
      setRows(r => [rest, ...r.filter(x => x.id !== rest.id)]);
      if (data.duplicate) {
        toast.info(`Already in your directory: ${rest.name} — matched by ${data.matched_on}`);
      } else {
        toast.success(`Imported ${rest.name}`);
        if ((data.suggested_tags || []).length > 0) {
          setSuggestOpen({ restaurant: rest, tags: data.suggested_tags });
        }
      }
    } catch (e) { toast.error(formatError(e)); }
  };

  const applySuggestedTags = async (chosen) => {
    if (!suggestOpen) return;
    try {
      await api.patch(`/restaurants/${suggestOpen.restaurant.id}`, { tags: [...(suggestOpen.restaurant.tags||[]), ...chosen] });
      toast.success(`Added ${chosen.length} tag(s)`);
      setSuggestOpen(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const fetchTagSuggestions = async (rest) => {
    try {
      const { data } = await api.get(`/restaurants/${rest.id}/tag-suggestions`);
      if ((data.suggested_tags || []).length === 0) toast.info("No new tags suggested from reviews");
      else setSuggestOpen({ restaurant: rest, tags: data.suggested_tags, hits: data.hits });
    } catch (e) { toast.error(formatError(e)); }
  };

  const nearHousehold = households.find(h => h.id === nearHouseholdId);
  const rowsWithDist = rows.map(r => ({
    ...r,
    _dist_miles: nearHousehold && r.latitude != null && r.longitude != null
      ? distanceMiles(nearHousehold.latitude, nearHousehold.longitude, r.latitude, r.longitude)
      : null,
  }));
  const cuisineOptions = Array.from(new Set(rows.map(r => r.cuisine).filter(Boolean))).sort();
  const q = nameFilter.trim().toLowerCase();
  const visible = rowsWithDist
    .filter(r => showInactive || r.active)
    .filter(r => !cuisineFilter || (r.cuisine || "") === cuisineFilter)
    .filter(r => !q || (r.name || "").toLowerCase().includes(q) || (r.cuisine || "").toLowerCase().includes(q) || (r.city || "").toLowerCase().includes(q))
    .filter(r => nearMiles === 0 || (r._dist_miles != null && r._dist_miles <= nearMiles))
    .sort((a, b) => nearMiles === 0 ? 0 : ((a._dist_miles ?? 1e9) - (b._dist_miles ?? 1e9)));

  // Reset the "Load more" window whenever filters change.
  useEffect(() => { setVisibleCount(60); }, [nameFilter, cuisineFilter, nearMiles, nearHouseholdId, showInactive]);
  const pageSize = 60;
  const shown = visible.slice(0, visibleCount);
  const hasMore = visible.length > shown.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Restaurant management</div>
          <h1 className="font-serif text-3xl sm:text-4xl">Restaurants</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm"><Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle"/> Show inactive</label>
          <Button variant="outline" onClick={runBulkLoad} disabled={!features.google_maps || bulkLoading} data-testid="bulk-load-btn" title="Sweep 50-mi radius from primary household">
            <Sparkles className="w-4 h-4 mr-1"/>{bulkLoading ? "Loading…" : "Bulk load 50 mi"}
          </Button>
          <Button variant="outline" onClick={()=>setDiscOpen(o=>!o)} data-testid="discover-toggle-btn" disabled={!features.google_maps}><Sparkles className="w-4 h-4 mr-1"/>{discOpen?"Close":"Discover nearby"}</Button>
          <Button onClick={()=>{setREdit(null); setROpen(true);}} data-testid="add-restaurant-btn">+ Add restaurant</Button>
        </div>
      </div>

      {rows.length < 100 && features.google_maps && (
        <Card className="p-4 border-primary/40 bg-primary/5" data-testid="empty-db-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">Only {rows.length} restaurants in this database.</div>
              <div className="text-xs text-muted-foreground mt-0.5">Each environment (preview vs live) has its own database. Tap <strong>Bulk load 50 mi</strong> to pull in every restaurant within 50 miles of your primary household — usually ~1000 places.</div>
            </div>
            <Button size="sm" onClick={runBulkLoad} disabled={bulkLoading} data-testid="empty-db-bulk-btn">
              <Sparkles className="w-4 h-4 mr-1"/>{bulkLoading ? "Loading…" : "Bulk load now"}
            </Button>
          </div>
        </Card>
      )}

      {bulkResult && (
        <Card className="p-3 text-xs mono bg-secondary/40" data-testid="bulk-load-result">
          Bulk load: +{bulkResult.inserted} added · {bulkResult.skipped_existing} skipped as duplicates · {bulkResult.total_found || bulkResult.places?.length || "?"} places scanned.
        </Card>
      )}

      <p className="text-xs text-muted-foreground">Deactivating a restaurant preserves all past visits, ratings, and comments — it just stops appearing in recommendations.</p>

      <Card className="p-4" data-testid="restaurants-filter-bar">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Search by name</Label>
            <Input value={nameFilter} onChange={e=>setNameFilter(e.target.value)} placeholder="Name, city…" data-testid="restaurants-name-filter"/>
          </div>
          <div>
            <Label className="text-xs">Type of restaurant</Label>
            <select value={cuisineFilter} onChange={e=>setCuisineFilter(e.target.value)}
                    className="w-full border rounded-md h-10 px-3 bg-background text-sm"
                    data-testid="restaurants-cuisine-filter">
              <option value="">All types ({cuisineOptions.length})</option>
              {cuisineOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Within (miles){nearHousehold ? <span className="text-muted-foreground font-normal"> · from {nearHousehold.name}</span> : ""}</Label>
            <div className="flex flex-wrap gap-1 mt-1" data-testid="near-miles-buttons">
              {[0, 5, 10, 20, 30, 50].map(m => (
                <button key={m} type="button" onClick={()=>setNearMiles(m)}
                        data-testid={`near-miles-${m}`}
                        className={`px-3 py-1.5 rounded-full border text-xs ${nearMiles===m?"bg-primary text-primary-foreground border-primary":"hover:bg-secondary"}`}>
                  {m === 0 ? "All" : `≤ ${m} mi`}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-2 mono flex items-center justify-between gap-2" data-testid="restaurants-filter-count">
          <span>Showing <strong>{shown.length}</strong> of {visible.length} restaurants{visible.length !== rows.length ? ` (${rows.length} total)` : ""}{cuisineFilter ? ` · ${cuisineFilter}` : ""}{nearMiles > 0 && nearHousehold ? ` · within ${nearMiles} mi of ${nearHousehold.name}` : ""}</span>
          {(nameFilter || cuisineFilter || nearMiles > 0) && (
            <button onClick={()=>{setNameFilter(""); setCuisineFilter(""); setNearMiles(0);}}
                    data-testid="clear-filters-btn"
                    className="text-primary hover:underline">Clear filters</button>
          )}
        </div>
      </Card>

      {!features.google_maps && (
        <Card className="p-4 text-xs text-muted-foreground"><span className="font-medium">Discover nearby</span> is off — set <code className="mono">GOOGLE_MAPS_API_KEY</code> in the backend to enable Google Places search.</Card>
      )}

      {discOpen && features.google_maps && (
        <Card className="p-6 space-y-4">
          <h3 className="font-serif text-xl">Discover restaurants near a household</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Center on</Label>
              <select value={discHouseholdId} onChange={e=>setDiscHouseholdId(e.target.value)} className="w-full border rounded-md h-10 px-3 bg-background" data-testid="discover-household-select">
                {households.filter(h=>h.active).map(h=><option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div><Label>Keyword</Label><Input value={discKeyword} onChange={e=>setDiscKeyword(e.target.value)} data-testid="discover-keyword-input"/></div>
            <div><Label>Radius (m)</Label><Input type="number" value={discRadius} onChange={e=>setDiscRadius(Number(e.target.value))}/></div>
            <div className="flex items-end"><Button onClick={discover} disabled={discLoading} data-testid="discover-search-btn" className="w-full">{discLoading?"Searching…":"Search"}</Button></div>
          </div>
          <div className="space-y-2">
            {discResults.map(p => (
              <div key={p.external_id} className="flex items-center justify-between gap-3 p-3 border rounded-lg" data-testid="discover-result-item">
                <div className="flex-1">
                  <div className="font-medium">{p.name} {p.rating && <span className="text-xs text-muted-foreground ml-2">★ {p.rating} ({p.user_ratings_total})</span>}</div>
                  <div className="text-xs text-muted-foreground flex gap-1 items-center"><MapPin className="w-3 h-3"/>{p.address}</div>
                </div>
                <Button size="sm" onClick={()=>importPlace(p)} data-testid="discover-import-btn">Import</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="restaurants-grid">
        {shown.map(r => (
          <Card key={r.id} className={`p-5 h-full ${!r.active?"opacity-60":""} flex flex-col justify-between`}>
            <div>
              <div className="flex items-start justify-between mb-1">
                <div className="overline">{r.cuisine} · {"$".repeat(r.price_level)}{!r.active && <span className="text-xs ml-2">inactive</span>}</div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={()=>fetchTagSuggestions(r)} data-testid="tag-suggest-btn" title="Get tag suggestions from reviews">✨</Button>
                    <Button size="icon" variant="ghost" onClick={()=>{setREdit(r); setROpen(true);}} data-testid="edit-restaurant-btn"><Pencil className="w-4 h-4"/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>toggleActive(r)} data-testid="toggle-restaurant-active-btn" title={r.active?"Deactivate":"Reactivate"}><Power className="w-4 h-4"/></Button>
                  </div>
              </div>
              <Link to={`/restaurants/${r.id}`}><div className="font-serif text-xl hover:text-primary">{r.name}</div></Link>
              <div className="text-sm mono text-muted-foreground mt-1">
                {r.city}, {r.state}
                {r._dist_miles != null && <span className="ml-2 text-primary">· {r._dist_miles.toFixed(1)} mi</span>}
              </div>
              {(r.ratings_series?.length || 0) >= 2 && (
                <div className="flex items-center gap-2 mt-2" data-testid="restaurant-rating-trend">
                  <Sparkline entries={r.ratings_series}/>
                  <span className="text-xs mono text-muted-foreground flex items-center gap-1">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400"/>
                    {(r.ratings_series.reduce((s, x) => s + x.stars, 0) / r.ratings_series.length).toFixed(1)}
                    <span className="text-[10px]">· {r.ratings_series.length}</span>
                  </span>
                </div>
              )}
              {r.preferred_provider && (
                <div className="mt-2 text-[10px] mono uppercase tracking-widest text-primary" data-testid="preferred-provider-hint">
                  Books on {r.preferred_provider === "opentable" ? "OpenTable" : "Resy"}
                </div>
              )}
              {r.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{r.tags.map(t => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary">{t.replace("_"," ")}</span>)}</div>}
            </div>
          </Card>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center" data-testid="restaurants-load-more-wrap">
          <Button variant="outline" onClick={()=>setVisibleCount(c => c + pageSize)} data-testid="restaurants-load-more-btn">
            Load {Math.min(pageSize, visible.length - shown.length)} more · {visible.length - shown.length} remaining
          </Button>
        </div>
      )}

      <RestaurantModal open={rOpen} onOpenChange={setROpen} initial={rEdit} onSaved={load} cuisineOptions={cuisineOptions}/>
      <TagSuggestModal state={suggestOpen} onClose={()=>setSuggestOpen(null)} onApply={applySuggestedTags}/>
    </div>
  );
}

function TagSuggestModal({ state, onClose, onApply }) {
  const [selected, setSelected] = useState(new Set());
  useEffect(() => { if (state) setSelected(new Set(state.tags)); }, [state]);
  if (!state) return null;
  const toggle = (t) => setSelected(s => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });
  return (
    <Dialog open={!!state} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Suggested tags for {state.restaurant.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Extracted from Google reviews. Pick which ones to add.</p>
        <div className="flex flex-wrap gap-2">
          {state.tags.map(t => {
            const count = state.hits?.[t];
            return (
              <button key={t} type="button" onClick={()=>toggle(t)} data-testid="tag-suggestion-btn"
                      className={`px-3 py-1.5 rounded-full text-sm border ${selected.has(t)?"bg-primary text-primary-foreground border-primary":"hover:bg-secondary"}`}>
                {t.replace("_"," ")}{count?` · ${count}`:""}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Skip</Button>
          <Button data-testid="apply-suggested-tags-btn" disabled={selected.size===0} onClick={()=>onApply([...selected])}>Add {selected.size} tag(s)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
